import { useEffect, useRef, useState, useCallback } from "react";


const HEARTBEAT_INTERVAL = 5000; 
const RECONNECT_DELAY = 5000;
const MAX_MESSAGES = 100;
const CONNECTION_TIMEOUT = 10000;
const CONNECTION_POLL_INTERVAL = 100;

 const ws_status ={
  CONNECTING: "connecting",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
  COMPLETED: "completed"
 }

 function buildWebSocketUrl(projectId) {
  const baseUrl = process.env.REACT_APP_BASE_URL || "http://3.139.107.45:8001";
  const isSecure = baseUrl.startsWith("https://");
  const wsProtocol = isSecure ? "wss" : "ws";
  const cleanBase = baseUrl.replace(/^https?:\/\//, "");
  return `${wsProtocol}://${cleanBase.replace(/\/+$/, "")}/ws/${projectId}`;
}

 function appendCapped(prev, item, limit = MAX_MESSAGES) {
  return [...prev, item].slice(-limit);
}


export default function useProjectSocket(projectId) {
  const ws = useRef(null);
  const [messages, setMessages] = useState([]);
  const [rawMessages, setRawMessages] = useState([]);
  const [status, setStatus] = useState("disconnected");
  const [progress, setProgress] = useState(0);
  const socketRef = useRef(null);

  

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ws.current) {
        if (ws.current.heartbeatInterval) clearInterval(ws.current.heartbeatInterval);
        ws.current.close();
        ws.current = null;
      }
    };
  }, []);

    const clearHeartbeat = () => {
    if (socketRef.current?.heartbeatInterval) {
      clearInterval(socketRef.current.heartbeatInterval);
    }
  };

    const closeSocket = useCallback(() => {
    if (!socketRef.current) return;
    clearHeartbeat();
    socketRef.current.close();
    socketRef.current = null;
  }, []);

    useEffect(() => {
    return () => closeSocket();
  }, [closeSocket]);

  // Close when progress completes
  // useEffect(() => {
  //   if (progress === 100 && ws.current) {
  //     console.log("[WS] Progress reached 100%. Closing connection...");
  //     if (ws.current.heartbeatInterval) clearInterval(ws.current.heartbeatInterval);
  //     ws.current.close();
  //     ws.current = null;
  //     setStatus("completed");
  //   }
  // }, [progress]);
    const startHeartbeat = useCallback(() => {
    socketRef.current.heartbeatInterval = setInterval(() => {
      const isOpen = socketRef.current?.readyState === WebSocket.OPEN;
      if (!isOpen) return;
 
      const ping = {
        event: "ping",
        project_id: projectId,
        message: { timestamp: new Date().toISOString() },
      };
 
      socketRef.current.send(JSON.stringify(ping));
      console.debug("[WS] Heartbeat sent");
    }, HEARTBEAT_INTERVAL);
  }, [projectId]);

    const handleOpen = useCallback(() => {
    console.info("[WS] Connection established");
    setStatus(ws_status.CONNECTED);
    startHeartbeat();
  }, [startHeartbeat]);
 
  const handleClose = useCallback(() => {
    console.warn("[WS] Connection closed");
    clearHeartbeat();
    setStatus(ws_status.DISCONNECTED);
 
    setTimeout(() => {
      console.info("[WS] Attempting reconnect...");
      setStatus(ws_status.RECONNECTING);
    }, RECONNECT_DELAY);
  }, []);
 
  const handleError = useCallback((error) => {
    console.error("[WS] Error:", error);
    setStatus(ws_status.ERROR);
  }, []);

    const handleMessage = useCallback((event) => {
    console.debug("[WS] Raw message received:", event.data);
 
    setRawMessages((prev) => appendCapped(prev, event.data));
 
    try {
      const data = JSON.parse(event.data);
      console.debug("[WS] data received from socket connection --->>:", data);
 
      if (data.type === "pong") {
        console.debug("[WS] Pong received");
        return;
      }
 
      if (data.message?.progress !== undefined) {
        setProgress(data.message.progress);
      }
 
      setMessages((prev) => appendCapped(prev, data));
    } catch {
      console.warn("[WS] Non-JSON message ignored:", event.data);
    }
  }, []);





    const connectWebSocket = useCallback(() => {
      if (!projectId) return;

      const alreadyConnected = ws.current && ws.current.readyState === WebSocket.OPEN;

      if (alreadyConnected) {
        console.log("[WS] Already connected");
        return;
      }

      const wsUrl = buildWebSocketUrl(projectId);

      console.log(`[WS] Connecting to ${wsUrl}...`);

      const socket = new WebSocket(wsUrl);
      socket.onopen = handleOpen;
      socket.onmessage = handleMessage;
      socket.onerror = handleError;
      socket.onclose = handleClose;
      

      socketRef.current = socket;

      console.log(`[WS] WebSocket instance created:`, ws.current);
    }, [projectId, handleOpen, handleClose, handleError, handleMessage]);

  const disconnectWebSocket = useCallback(() => {
    console.info("[WS] Manually disconnecting");
    closeSocket();
    setStatus(ws_status.DISCONNECTED);
  }, [closeSocket]);

// });

   const sendEvent = useCallback(
    (event, message = {}) => {
       console.log('[DEBUG] sendEvent called, readyState:', socketRef.current?.readyState)
      const isOpen = socketRef.current?.readyState === WebSocket.OPEN;
      if (!isOpen) {
        console.warn("[WS] Cannot send — socket is not open");
        return;
      }
 
      const payload = { event, project_id: projectId, message };
      socketRef.current.send(JSON.stringify(payload));
      console.debug("[WS] Sent:", payload);
    },
    [projectId]
  );


    const syncEpics = useCallback(() => {
    sendEvent("sync_epics", {
      project_id: projectId,
      message: "Start syncing epics...",
    });
  }, [sendEvent, projectId]);


     const waitForConnection = useCallback(
    (timeout = CONNECTION_TIMEOUT) => {
      return new Promise((resolve, reject) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          resolve(true);
          return;
        }
 
        const timer = setTimeout(
          () => reject(new Error("[WS] Connection timed out")),
          timeout
        );
 
        if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
          connectWebSocket();
        }
 
        const poll = () => {
          const state = socketRef.current?.readyState;
 
          if (state === WebSocket.OPEN) {
            clearTimeout(timer);
            resolve(true);
          } else if (state === WebSocket.CLOSED || !socketRef.current) {
            clearTimeout(timer);
            reject(new Error("[WS] Connection closed before opening"));
          } else {
            setTimeout(poll, CONNECTION_POLL_INTERVAL);
          }
        };
 
        setTimeout(poll, CONNECTION_POLL_INTERVAL);
      });
    },
    [connectWebSocket]
  );


   const resetProgress = useCallback(() => setProgress(0), []);

   const clearMessages = useCallback(() => {
     setMessages([]);
     setRawMessages([]);
   }, []);

    return {
      status,
      messages,
      rawMessages,
      sendEvent,
      syncEpics,
      progress,
      connectWebSocket,
      waitForConnection,
      resetProgress,
      clearMessages,
      disconnectWebSocket
    };
 ;

}
